export interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  categoryName: string;
  author: string;
  date: string;
  readTime: number;
  image: string;
  tags: string[];
  featured?: boolean;
}

export const articles: Article[] = [
  {
    id: "article-1",
    title: "如何用AI辅助撰写高质量视频脚本：从零到一的全流程指南",
    excerpt: "在视频创作中，脚本是灵魂。本文详细讲解如何借助AI工具快速产出专业级视频脚本，大幅提升创作效率。",
    content: `<h2>引言</h2><p>在视频创作领域，脚本是整个作品的蓝图和灵魂。一个优秀的视频脚本不仅能够清晰地传达信息，还能在情感层面与观众产生共鸣。随着人工智能技术的快速发展，AI辅助脚本撰写已经成为提升创作效率的重要手段。</p><h2>为什么需要视频脚本</h2><p>很多创作者认为即兴发挥更能展现真实感，但事实上，有脚本的视频往往在结构完整性、信息传达效率和观众留存率方面表现更优。一个好的脚本能够：</p><ul><li>明确视频的核心信息和目标受众</li><li>合理分配每个段落的时间</li><li>确保视觉和听觉元素的协调统一</li><li>提升拍摄效率，减少后期返工</li></ul><h2>AI辅助脚本撰写的四个步骤</h2><h3>第一步：明确视频目标</h3><p>在使用AI工具之前，创作者需要先明确视频的目标——是品牌宣传、产品介绍、知识科普还是娱乐内容？不同的目标决定了脚本的语调、节奏和结构。</p><h3>第二步：搭建脚本框架</h3><p>一个好的脚本框架通常包含：开场钩子（前5秒吸引注意）、主体内容（核心信息分段呈现）、行动号召（引导观众下一步行为）。AI可以帮助你快速生成多种框架方案供选择。</p><h3>第三步：丰富内容细节</h3><p>在框架基础上，AI可以根据你的需求填充具体的台词、场景描述、镜头建议等内容。关键在于提供足够清晰的指令和参考素材。</p><h3>第四步：优化与个性化</h3><p>AI生成的初稿需要经过人工润色，加入个人风格和品牌特色。这是让脚本从"能用"到"出彩"的关键一步。</p><h2>常见误区与解决方案</h2><p>很多创作者在使用AI工具时会遇到内容同质化的问题。解决方法是提供独特的品牌语料和个人风格描述，让AI更好地理解你的创作风格。此外，不要完全依赖AI——它是最好的助手，但最终的创意决策权永远在创作者手中。</p><h2>结语</h2><p>AI辅助脚本撰写不是要取代创作者的思考，而是将创作者从重复劳动中解放出来，让他们有更多精力投入到创意本身。17起草视频正是为此而生——让每一个创作者都能高效产出优质内容。</p>`,
    category: "video-script",
    categoryName: "视频脚本",
    author: "李明哲",
    date: "2026-07-28",
    readTime: 8,
    image: "https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20laptop%20showing%20video%20editing%20timeline%20and%20script%20document%2C%20warm%20ambient%20lighting%2C%20professional%20content%20creator%20desk%2C%20clean%20and%20organized%2C%20soft%20shadows%2C%20minimalist%20aesthetic%20with%20wooden%20desk%20and%20plants&width=800&height=500&seq=article-1&orientation=landscape",
    tags: ["AI写脚本", "视频脚本", "创作效率", "AI工具"],
    featured: true,
  },
  {
    id: "article-2",
    title: "素材管理终极指南：告别混乱，建立高效视频素材库",
    excerpt: "视频素材越来越多，找素材越来越慢？本文分享一套科学的素材管理方法，让你的创作流程事半功倍。",
    content: `<h2>素材管理的痛点</h2><p>随着视频创作频率的提高，素材文件的数量呈指数级增长。无论是专业团队还是个人创作者，都面临着一个共同的问题：如何在海量素材中快速找到所需的内容？根据行业调查，视频创作者平均每周花费约3.5小时在素材查找和整理上——这些时间本可以用来创作。</p><h2>建立科学的文件命名体系</h2><p>一个好的命名体系是素材管理的基础。建议采用"日期_项目名_场景_版本号"的格式，例如：20260715_品牌宣传片_办公室场景_v2。这种命名方式让文件在排序时自动按时间和项目分组，查找效率大幅提升。</p><h2>分级分类策略</h2><p>按照使用频率和重要性，可以将素材分为三个层级：</p><ul><li><strong>核心素材</strong>：经常使用的品牌元素、LOGO、片头片尾等，放在最方便的快速访问区</li><li><strong>项目素材</strong>：当前正在进行项目的相关素材，按项目独立建文件夹</li><li><strong>归档素材</strong>：已完成项目的素材，压缩后存入归档区，需要时再调取</li></ul><h2>利用标签系统提升检索效率</h2><p>现代素材管理工具普遍支持标签功能。建议建立一套统一的标签体系，包括素材类型（空镜/访谈/产品/B-Roll）、情绪调性（温馨/激昂/冷静）、场景类型（室内/户外/演播室）等维度。标签之间可以交叉组合，实现精准检索。</p><h2>17起草视频的智能素材管理</h2><p>17起草视频平台内置了AI驱动的智能素材整理功能，可以自动识别视频内容并打上标签，甚至能根据脚本内容智能推荐匹配的素材片段，让素材管理从繁琐的手工操作升级为智能化的自动流程。</p>`,
    category: "material-organize",
    categoryName: "素材整理",
    author: "王思涵",
    date: "2026-07-25",
    readTime: 10,
    image: "https://readdy.ai/api/search-image?query=Organized%20digital%20asset%20management%20dashboard%20with%20video%20thumbnails%20and%20folder%20structure%2C%20modern%20UI%20design%2C%20warm%20neutral%20tones%2C%20clean%20interface%2C%20professional%20software%20screenshot%20style&width=800&height=500&seq=article-2&orientation=landscape",
    tags: ["素材管理", "创作效率", "文件管理", "视频制作"],
    featured: true,
  },
  {
    id: "article-3",
    title: "短视频时代的剪辑秘籍：10个让视频质感翻倍的技巧",
    excerpt: "同样的素材，为什么别人的视频看起来更高级？掌握这些剪辑技巧，让你的短视频质感瞬间提升。",
    content: `<h2>剪辑是视频的二次创作</h2><p>拍摄只是获取原料，真正让视频焕发生命力的是剪辑环节。在短视频时代，观众的前3秒耐心决定了视频的生死，因此剪辑技巧的重要性被提到了前所未有的高度。</p><h2>技巧一：节奏感是剪辑的灵魂</h2><p>好的剪辑节奏就像音乐一样有起承转合。一般来说，短视频的平均镜头时长应控制在2-4秒，重要信息镜头可适当延长到5-7秒。快慢结合才能让观众保持注意力。</p><h2>技巧二：跳切的艺术</h2><p>跳切（Jump Cut）是短视频中最常用的技法之一，通过在连续性镜头中有意制造跳跃感来制造节奏和趣味。关键在于跳切的时机——通常选择在句子之间的自然停顿处。</p><h2>技巧三：声音设计不容忽视</h2><p>声音对视频观感的影响占到了50%以上。合适的背景音乐、恰到好处的音效、清晰的旁白——这些声音元素的质量直接决定了视频的专业度。</p><h2>技巧四：色彩统一是高级感的基础</h2><p>给视频套用统一的LUT或调色预设，让不同场景的素材在色彩上保持协调。暖色调适合温馨内容，冷色调适合科技感，高对比度适合时尚类内容。</p><h2>更多技巧...</h2><p>还有过渡动画的克制使用、文字动画的节奏控制、画面构图的重构等等，每一项都能为你的视频增色不少。</p>`,
    category: "short-video",
    categoryName: "短视频",
    author: "张伟杰",
    date: "2026-07-22",
    readTime: 7,
    image: "https://readdy.ai/api/search-image?query=Video%20editing%20software%20interface%20with%20timeline%20and%20color%20grading%20panel%2C%20professional%20setup%20with%20warm%20desk%20lamp%2C%20creative%20atmosphere%2C%20modern%20workspace&width=800&height=500&seq=article-3&orientation=landscape",
    tags: ["剪辑技巧", "短视频", "后期制作", "创作教程"],
    featured: false,
  },
  {
    id: "article-4",
    title: "商业宣传片策划全攻略：让品牌故事打动人心",
    excerpt: "商业视频不是简单地介绍产品，而是讲述一个能引发共鸣的品牌故事。本文从策划角度拆解商业视频的成功要素。",
    content: `<h2>商业视频的核心是讲故事</h2><p>在信息过载的时代，单纯的广告信息已经无法吸引消费者的注意力。真正有效的商业视频，需要用一个好故事来承载品牌信息，让观众在情感共鸣中自然地接受品牌理念。</p><h2>品牌故事的三要素</h2><p>一个完整的品牌故事应该包含：<strong>冲突</strong>（用户面临的问题或痛点）、<strong>解决方案</strong>（你的产品如何解决问题）、<strong>转变</strong>（用户使用产品后的美好变化）。这个框架适用于从30秒广告到10分钟品牌纪录片的任何商业视频。</p><h2>目标受众画像</h2><p>在策划阶段，必须明确视频的目标受众是谁。年龄、职业、消费习惯、内容偏好——这些信息决定了视频的语调、视觉风格和发布渠道。建议制作详细的用户画像卡片，让整个制作团队对目标受众有统一认知。</p><h2>创意简报的撰写</h2><p>创意简报是连接客户需求和创意执行的关键文档。一份合格的创意简报应包含：项目背景、目标受众、核心信息、品牌语调、参考案例、交付物清单和时间节点。</p><h2>17起草视频的商业视频解决方案</h2><p>17起草视频为商业视频策划提供了完整的模板库和案例库，覆盖企业宣传片、产品介绍视频、品牌故事片、客户证言视频等多种类型，帮助品牌方和制作方高效对齐需求。</p>`,
    category: "commercial-video",
    categoryName: "商业视频",
    author: "陈雅琪",
    date: "2026-07-20",
    readTime: 9,
    image: "https://readdy.ai/api/search-image?query=Professional%20commercial%20video%20production%20meeting%20with%20storyboard%20on%20wall%2C%20creative%20team%20discussing%2C%20modern%20office%20with%20warm%20lighting%2C%20clean%20corporate%20style&width=800&height=500&seq=article-4&orientation=landscape",
    tags: ["商业视频", "品牌故事", "视频策划", "企业宣传"],
    featured: true,
  },
  {
    id: "article-5",
    title: "从灵感到成片：视频创作全流程拆解与效率提升方法",
    excerpt: "一个视频从想法到最终成片，中间经历了哪些环节？本文为您梳理完整流程并提供每个环节的提效建议。",
    content: `<h2>视频创作是一个系统工程</h2><p>很多新手创作者认为视频制作就是拍摄和剪辑，但实际上一个完整的视频创作流程包含了策划、筹备、拍摄、后期、发布五大阶段，每个阶段又包含了多个子环节。了解全流程是提升效率的前提。</p><h2>第一阶段：策划与脚本</h2><p>这是整个流程中最容易被忽视但最重要的环节。确定视频目标、研究受众、撰写脚本、设计分镜——在这个阶段投入的时间会在后续环节中得到成倍的回报。</p><h2>第二阶段：筹备与预制作</h2><p>包括场地勘查、道具准备、人员调度、设备检查等。对于团队项目，这个阶段还需要制定详细的拍摄计划和应急预案。</p><h2>第三阶段：拍摄执行</h2><p>严格按照分镜脚本执行拍摄，同时保持一定的灵活性以捕捉计划外的精彩瞬间。关键是要确保素材的多样性和充足性。</p><h2>第四阶段：后期制作</h2><p>从粗剪到精剪，从调色到混音，从特效到字幕——后期是赋予视频最终形态的环节。合理的工作流程是先完成结构再雕琢细节。</p><h2>第五阶段：发布与优化</h2><p>选择合适的发布平台和时间，撰写吸引人的标题和封面，并根据数据反馈持续优化后续内容。</p>`,
    category: "creative-planning",
    categoryName: "创意策划",
    author: "赵明远",
    date: "2026-07-18",
    readTime: 11,
    image: "https://readdy.ai/api/search-image?query=Complete%20video%20production%20workflow%20visualization%20from%20idea%20to%20final%20cut%2C%20creative%20process%20illustration%2C%20warm%20inspiring%20colors%2C%20modern%20design%20style&width=800&height=500&seq=article-5&orientation=landscape",
    tags: ["创作流程", "效率提升", "视频制作", "全流程"],
    featured: false,
  },
  {
    id: "article-6",
    title: "如何写出让人停不下来的视频开头：开篇钩子设计方法",
    excerpt: "前3秒决定观众的去留。掌握开篇钩子的设计方法，让你的视频从第一秒就牢牢抓住观众的注意力。",
    content: `<h2>开头3秒即决胜负</h2><p>在信息流环境中，观众的注意力极其稀缺。数据显示，如果视频前3秒不能引起观众的兴趣，超过60%的人会选择滑走。因此，设计一个强有力的开篇钩子是每个视频创作者必须掌握的技能。</p><h2>七种高效开篇钩子</h2><h3>1. 疑问式开头</h3><p>用一个直击痛点的问题开场，让观众产生"这正是我想知道的"的感觉。例如："你知道为什么你的视频总是没人看吗？"</p><h3>2. 反常识开头</h3><p>抛出一个与常识相悖的观点或数据，制造认知冲突。例如："每天发3条视频，不如每周发1条。"</p><h3>3. 结果前置</h3><p>先把最好的结果展示出来，让观众想知道"这是怎么做到的"。适合教程类和案例分享类视频。</p><h3>4. 故事开头</h3><p>用一个简短的故事开场，利用人类对故事的天生好奇心留住观众。故事越长越需要在前两句就建立悬念。</p><h3>5. 痛点共鸣</h3><p>准确描述目标受众正在经历的困扰，建立"你懂我"的连接感。</p><h3>6. 视觉冲击</h3><p>用一个极具视觉冲击力的画面或特效开场，适合视觉类内容。</p><h3>7. 承诺价值</h3><p>明确告诉观众看完这个视频能得到什么，让价值预期驱动观看行为。</p><h2>如何选择适合的钩子类型</h2><p>选择钩子类型需要综合考虑：内容类型（教程/娱乐/观点）、目标平台（抖音/B站/YouTube）、受众特征和品牌调性。没有放之四海皆准的万能钩子，只有最适合你内容的那一款。</p>`,
    category: "video-script",
    categoryName: "视频脚本",
    author: "李明哲",
    date: "2026-07-15",
    readTime: 6,
    image: "https://readdy.ai/api/search-image?query=Video%20creator%20writing%20script%20with%20creative%20storyboard%20on%20desk%2C%20warm%20ambient%20light%2C%20focused%20atmosphere%2C%20modern%20workspace%20with%20notebook%20and%20coffee&width=800&height=500&seq=article-6&orientation=landscape",
    tags: ["开篇技巧", "视频脚本", "观众留存", "创作技巧"],
    featured: false,
  },
  {
    id: "article-7",
    title: "B-Roll拍摄完全指南：让你的视频画面更丰富更有层次",
    excerpt: "B-Roll是提升视频质感的秘密武器。本文从拍摄到运用，全面解析B-Roll的创作方法。",
    content: `<h2>什么是B-Roll</h2><p>B-Roll是指补充主镜头（A-Roll）的辅助画面素材。在采访或演讲类视频中，A-Roll是讲话者的画面，B-Roll则是用来覆盖剪辑点、丰富视觉效果的相关画面。优秀的B-Roll能让视频的观感提升数个档次。</p><h2>B-Roll的五大作用</h2><p>B-Roll不仅仅是用来"填空"的，它在视频中承担着多重功能：覆盖剪辑点使过渡自然、提供视觉信息补充口播内容、营造氛围和情绪、建立场景感和空间感、提升视频的专业度和制作水准。</p><h2>B-Roll拍摄技巧</h2><p>拍摄B-Roll时，建议遵循"多点位、多角度、多景别"原则。同一个场景至少从3个不同角度拍摄，包含全景、中景、特写三个景别。每个镜头持续拍摄8-15秒，留够剪辑空间。使用稳定器或三脚架确保画面稳定。</p><h2>B-Roll的后期运用</h2><p>在剪辑时，B-Roll应该与A-Roll的内容紧密配合。通常的做法是：在讲到关键信息时切入相关B-Roll画面，每个B-Roll片段持续2-4秒，通过变速和转场让画面节奏与旁白节奏协调。</p>`,
    category: "post-production",
    categoryName: "后期制作",
    author: "张伟杰",
    date: "2026-07-12",
    readTime: 7,
    image: "https://readdy.ai/api/search-image?query=Cinematic%20B-Roll%20footage%20being%20filmed%20with%20professional%20camera%2C%20shallow%20depth%20of%20field%2C%20warm%20natural%20light%2C%20creative%20filmmaking%20setup&width=800&height=500&seq=article-7&orientation=landscape",
    tags: ["B-Roll", "拍摄技巧", "后期制作", "视频质感"],
    featured: false,
  },
  {
    id: "article-8",
    title: "视频色彩心理学：如何用调色传达正确的情感和品牌调性",
    excerpt: "色彩不是简单的视觉装饰，它是与观众潜意识沟通的强大工具。了解色彩心理学，让调色成为叙事的一部分。",
    content: `<h2>色彩即情感</h2><p>在视频创作中，色彩不仅是视觉元素，更是情感传递的载体。不同的色彩组合能唤起观众不同的情绪反应——温暖的橙色传递活力和友好，冷静的蓝色表达专业和信任，深沉的暗色调营造神秘和高端感。</p><h2>常见的色彩情绪映射</h2><ul><li><strong>暖色调（橙/红/黄）</strong>：热情、活力、温馨、食欲——适合美食、生活方式、社交类内容</li><li><strong>冷色调（蓝/青/紫）</strong>：专业、冷静、科技、信任——适合科技产品、企业宣传、医疗健康类内容</li><li><strong>自然色调（绿/棕/米）</strong>：自然、健康、环保、有机——适合户外运动、环保主题、有机食品类内容</li><li><strong>高对比度</strong>：时尚、前卫、戏剧化——适合时尚、艺术、音乐类内容</li><li><strong>低饱和柔和</strong>：怀旧、温暖、文艺、治愈——适合生活方式、情感故事、文艺类内容</li></ul><h2>建立品牌调色体系</h2><p>对于持续输出内容的品牌或创作者，建议建立一套统一的调色体系。这不仅包括基础的调色预设（LUT），还包括在不同场景下的色彩规范——比如室内访谈用什么调色、户外活动用什么调色、产品展示用什么调色。统一的色彩体系能强化品牌识别度。</p>`,
    category: "post-production",
    categoryName: "后期制作",
    author: "陈雅琪",
    date: "2026-07-10",
    readTime: 8,
    image: "https://readdy.ai/api/search-image?query=Color%20grading%20panel%20with%20warm%20and%20cool%20color%20wheels%20showing%20video%20before%20and%20after%2C%20professional%20post-production%20workspace%2C%20cinematic%20monitor&width=800&height=500&seq=article-8&orientation=landscape",
    tags: ["色彩心理", "调色技巧", "后期制作", "品牌视觉"],
    featured: false,
  },
  {
    id: "article-9",
    title: "短视频平台的算法逻辑解析：如何让内容获得更多推荐",
    excerpt: "了解平台的推荐算法逻辑，不是投机取巧，而是让优质内容找到对的观众。深度解析主流平台的算法机制。",
    content: `<h2>理解算法才能更好地创作</h2><p>很多创作者对平台算法抱有抵触心理，认为算法让创作变得功利。但实际上，算法的根本目标是连接优质内容和感兴趣的用户。了解算法逻辑，不是投机取巧，而是让你的好内容有机会被更多人看到。</p><h2>算法关注的核心指标</h2><p>虽然不同平台的具体算法有所差异，但核心评估指标大致相同：<strong>完播率</strong>（观众看了多少）、<strong>互动率</strong>（点赞、评论、分享、收藏）、<strong>关注转化</strong>（看完后是否关注）、<strong>停留时长</strong>（在平台的总停留时间）。其中完播率的权重最高。</p><h2>内容优化的实操建议</h2><p>基于算法逻辑，创作者可以从以下方面优化内容：前3秒设置强钩子提升完播率、在视频中段设置"转折点"防止观众流失、结尾设置明确的互动引导、保持稳定的更新频率建立账号权重、选择与内容高度匹配的话题标签。</p><h2>不要为了算法牺牲内容质量</h2><p>追热点、标题党、蹭流量——这些短期行为可能带来暂时的数据增长，但长期来看，只有持续输出有价值的内容才能建立真正的粉丝基础。算法会变，好内容的价值不变。</p>`,
    category: "short-video",
    categoryName: "短视频",
    author: "赵明远",
    date: "2026-07-08",
    readTime: 9,
    image: "https://readdy.ai/api/search-image?query=Digital%20analytics%20dashboard%20showing%20social%20media%20video%20performance%20metrics%20with%20graphs%2C%20warm%20modern%20UI%2C%20data%20visualization%20on%20screen&width=800&height=500&seq=article-9&orientation=landscape",
    tags: ["平台算法", "短视频运营", "内容推荐", "流量密码"],
    featured: false,
  },
  {
    id: "article-10",
    title: "视频字幕设计指南：字体、排版与动画的最佳实践",
    excerpt: "字幕不只是文字的呈现，它是视频视觉设计的重要组成部分。学会字幕设计，让视频的专业度再上台阶。",
    content: `<h2>字幕是视频的门面</h2><p>在短视频盛行的时代，字幕已经从"辅助功能"变成了"标配元素"。超过80%的用户在静音状态下观看视频，这使得字幕的质量直接影响着内容的传播效果。好的字幕设计能让观众即使不看画面也能完整理解内容。</p><h2>字体选择原则</h2><p>视频字幕的字体选择需要优先考虑可读性。无衬线字体（如思源黑体、苹方）是首选，因为它们在小字号下依然清晰可辨。避免使用过于花哨的装饰性字体，除非是特定的标题设计用途。字号方面，在1920x1080分辨率下，正文字幕建议使用36-48px。</p><h2>字幕排版规范</h2><p>每行字幕的字符数建议不超过20个字（中文），确保观众能在一瞥之间读完。断句要遵循语义自然停顿，不要为了凑行数而硬生生把词组拆开。字幕位置通常放在画面底部偏上10%的位置，避免被播放器进度条遮挡。</p><h2>字幕动画的节奏感</h2><p>逐字出现（打字机效果）和高亮关键字是两种最流行的字幕动画。打字机效果的节奏要与语速同步，高亮关键词常用于强调核心信息或制造视觉节奏。动画不宜过于复杂——简洁利落才是高级感的关键。</p>`,
    category: "post-production",
    categoryName: "后期制作",
    author: "王思涵",
    date: "2026-07-05",
    readTime: 6,
    image: "https://readdy.ai/api/search-image?query=Video%20editing%20screen%20showing%20subtitle%20design%20with%20Chinese%20typography%2C%20modern%20clean%20layout%2C%20warm%20color%20palette%2C%20professional%20video%20production%20workspace&width=800&height=500&seq=article-10&orientation=landscape",
    tags: ["字幕设计", "字体排版", "后期制作", "视频美学"],
    featured: false,
  },
  {
    id: "article-11",
    title: "一人团队的视频创作效率手册：工具、流程与时间管理",
    excerpt: "不需要大团队也能高效产出优质视频内容。本文分享一人团队的创作实战经验，让有限的时间产出最大的价值。",
    content: `<h2>一人团队的独特挑战</h2><p>在个体创作者和自由职业者日益增多的今天，一人撑起整个视频内容产出的情况越来越普遍。没有团队协作就意味着需要同时扮演策划、导演、摄影、剪辑、运营等多个角色。这种情况下，效率管理比技能提升更为紧迫。</p><h2>必备工具清单</h2><p>经过大量实践验证，我们总结出一人团队最精简而高效的工具组合：17起草视频（脚本与素材管理）、剪映/CapCut或DaVinci Resolve（剪辑与调色）、Notion或飞书（项目管理）、Canva（封面设计）。不需要追求工具的数量，关键是每个工具都用得足够熟练。</p><h2>时间块管理法</h2><p>将一周的时间划分为固定的创作时段和运营时段。例如：周一周二集中策划和撰写脚本，周三周四拍摄，周五集中剪辑，周末安排发布和数据分析。固定时间做固定事情，减少任务切换带来的认知损耗。</p><h2>批量生产的威力</h2><p>如果条件允许，尽量采用批量生产方式：一次策划一个系列的多期内容、一次拍摄多期的素材、一次剪辑完成多期的初稿。规模化带来的效率提升是几何级的。</p>`,
    category: "creative-planning",
    categoryName: "创意策划",
    author: "赵明远",
    date: "2026-07-02",
    readTime: 8,
    image: "https://readdy.ai/api/search-image?query=Solo%20content%20creator%20working%20in%20home%20studio%20with%20camera%20and%20lighting%20setup%2C%20warm%20cozy%20atmosphere%2C%20minimalist%20workspace%2C%20creative%20vibe&width=800&height=500&seq=article-11&orientation=landscape",
    tags: ["个人创作", "效率提升", "工具推荐", "时间管理"],
    featured: false,
  },
  {
    id: "article-12",
    title: "企业视频营销ROI最大化策略：从策划到投放的全链路优化",
    excerpt: "视频营销预算有限，如何让每一分钱都花在刀刃上？本文从策略层面拆解企业视频营销的ROI提升方法。",
    content: `<h2>视频营销的ROI困境</h2><p>视频营销已经成为企业获客的重要手段，但很多企业在投入了大量预算后却发现效果不如预期。问题往往不在于视频制作本身，而在于缺乏系统性的策略规划。</p><h2>明确视频在营销漏斗中的角色</h2><p>不同阶段的视频有不同的目标：认知阶段需要的是广泛传播的爆款内容，考虑阶段需要的是深度讲解的产品视频，决策阶段需要的是打消顾虑的客户证言。在策划阶段就要想清楚这个视频在营销漏斗中的位置。</p><h2>一鱼多吃的素材策略</h2><p>一次拍摄，多平台分发。在拍摄时就规划好不同平台的剪辑版本——横版用于官网和YouTube、竖版用于抖音和快手、15秒精华版用于信息流广告、3分钟完整版用于详情页。让同一批素材的价值最大化。</p><h2>数据驱动的优化迭代</h2><p>视频发布不是结束，而是优化的开始。关注完播率、点击率、转化率三个核心指标，通过A/B测试不断优化视频的开头、中间和结尾。每一次的微小改进，累积起来就是显著的ROI提升。</p>`,
    category: "commercial-video",
    categoryName: "商业视频",
    author: "陈雅琪",
    date: "2026-06-28",
    readTime: 9,
    image: "https://readdy.ai/api/search-image?query=Corporate%20marketing%20team%20analyzing%20video%20performance%20data%20on%20large%20screen%2C%20modern%20meeting%20room%2C%20warm%20professional%20atmosphere%2C%20business%20analytics&width=800&height=500&seq=article-12&orientation=landscape",
    tags: ["视频营销", "ROI优化", "商业视频", "企业营销"],
    featured: false,
  },
];

export const getArticlesByCategory = (slug: string) => {
  return articles.filter((a) => a.category === slug);
};

export const getArticleById = (id: string) => {
  return articles.find((a) => a.id === id);
};

export const getFeaturedArticles = () => {
  return articles.filter((a) => a.featured);
};

export const getRelatedArticles = (currentId: string, category: string, limit = 3) => {
  return articles
    .filter((a) => a.category === category && a.id !== currentId)
    .slice(0, limit);
};